import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SubscriptionDashboard } from '../components/SubscriptionDashboard';
import { SubscriptionTiers } from '../components/SubscriptionTiers';
import { UsageQuotaDisplay } from '../components/UsageQuotaDisplay';
import { useAuth } from '../contexts/AuthContext';
import { Shield } from 'lucide-react';

type TabType = 'overview' | 'plans' | 'usage';

export default function Subscription() {
  const { isAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as TabType | null;
  const [activeTab, setActiveTab] = useState<TabType>(tabParam || 'overview');

  useEffect(() => {
    if (tabParam && ['overview', 'plans', 'usage'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isAdmin && (
          <div className="mb-6 bg-amber-50 border-2 border-amber-200 rounded-lg p-4 flex items-start gap-3">
            <Shield className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-900">Admin Full Access</h3>
              <p className="text-sm text-amber-800 mt-1">
                You have unrestricted access to all features. No subscription or payment requirements apply to your account.
              </p>
            </div>
          </div>
        )}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Subscription Management</h1>
          <p className="text-gray-600 mt-2">
            Manage your subscription, view usage, and upgrade your plan
          </p>
        </div>

        <div className="border-b border-gray-200 mb-8">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'overview'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('plans')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'plans'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Plans &amp; Pricing
            </button>
            <button
              onClick={() => setActiveTab('usage')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'usage'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Usage &amp; Quotas
            </button>
          </nav>
        </div>

        {activeTab === 'overview' && <SubscriptionDashboard />}

        {activeTab === 'plans' && <SubscriptionTiers />}

        {activeTab === 'usage' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Monthly Usage</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <UsageQuotaDisplay
                  quotaType="report_runs"
                  title="Report Runs"
                  description="Career intelligence reports generated"
                />
                <UsageQuotaDisplay
                  quotaType="ai_coach_messages"
                  title="AI Coach Messages"
                  description="Messages sent to AI career coach"
                />
                <UsageQuotaDisplay
                  quotaType="job_match_analyses"
                  title="Job Match Analyses"
                  description="Job descriptions analyzed"
                />
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="font-semibold text-blue-900 mb-2">Need More?</h3>
              <p className="text-blue-800 mb-4">
                Upgrade your plan to get higher quotas and unlock additional features.
              </p>
              <button
                onClick={() => setActiveTab('plans')}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                View Plans
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
